import Box from '@mui/material/Box';
import { type TooltipProps } from '@mui/material/Tooltip';
import {
  cloneElement,
  type HTMLAttributes,
  type JSX,
  type LegacyRef,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react';

import { useTouchClick } from '../../hooks';
import type { IconProps, IconTypes } from '@doxyme/icons';
import { type ThemeNames, useTheme } from '../../theme';
import { Tooltip } from '../tooltip';
import {
  getBaseStyles,
  getDestructiveStylesForType,
  getStylesBySize,
  getStylesByType,
} from './iconButton.styles';

export type IconButtonSize = 'xSmall' | 'small' | 'medium' | 'large';

export type IconButtonType =
  | 'default'
  | 'defaultAlpha'
  | 'subtle'
  | 'tinted'
  | 'tintedWithOutline';

export type IconButtonProps = HTMLAttributes<HTMLButtonElement> & {
  type?: IconButtonType;
  size?: IconButtonSize;
  className?: string;
  disabled?: boolean;
  icon: ReactNode;
  title?: string;
  onClick?: (e: MouseEvent) => void | Promise<void>;
  innerRef?: LegacyRef<HTMLButtonElement | null>;
  destructive?: boolean;
  themeName?: ThemeNames;
  children?: ReactNode;
  tooltipPlacement?: TooltipProps['placement'];
  active?: boolean;
  'aria-label'?: string;
  iconType?: IconTypes;
  canShowTooltip?: boolean;
} & (
    | { title: string; 'aria-label'?: string }
    | { title?: undefined; 'aria-label': string }
  );

export const IconButton = ({
  icon,
  size = 'small',
  disabled = false,
  onClick,
  type = 'default',
  innerRef,
  title,
  destructive = false,
  themeName,
  tooltipPlacement = 'top',
  active = false,
  iconType = 'stroke',
  canShowTooltip = true,
  ...rest
}: IconButtonProps): JSX.Element => {
  const theme = useTheme(themeName);

  const handleClick = (event: MouseEvent) => {
    if (disabled) {
      return;
    }

    void onClick?.(event);
  };

  const { handleTouchStart, handleTouchMove, handleTouchEnd } = useTouchClick({
    onClick: onClick ? handleClick : undefined,
  });

  const button = (
    <button
      aria-label={title}
      {...rest}
      disabled={disabled}
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      type='button'
      ref={innerRef}
      css={{
        ...getBaseStyles(theme),
        ...getStylesBySize(size, theme),
        ...getStylesByType(type, active, theme),
        ...(destructive
          ? getDestructiveStylesForType(type, active, theme)
          : {}),
        cursor: disabled ? 'not-allowed' : 'pointer',
      }}
    >
      {cloneElement(icon as ReactElement<IconProps>, {
        type: iconType,
      })}
    </button>
  );

  if (Boolean(title) && canShowTooltip) {
    return (
      <Tooltip placement={tooltipPlacement} title={title} offset={-4}>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          {button}
        </Box>
      </Tooltip>
    );
  }

  return button;
};
